// components/orders/OrderCard.jsx
import React, { useState } from 'react';
import OrderModal from './OrderModal';
import { ArrowRight } from '../../public/icons';
import { MemoryMagnet } from '../../public/images/home';

const OrderCard = ({ order, status }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  /* Normalize status — API sends `orderStatus`; old sample used `status` */
  const resolvedStatus = order.orderStatus || order.status || status;

  /* Normalize items — API sends objects; old sample sent strings */
  const items = Array.isArray(order.items) ? order.items : [];

  /* ── Future gifts — pull from the real API shape ── */
  const futureGifts =
    order.items?.[0]?.customization?.futureGift?.futureGifts || [];
  const hasFutureGifts = futureGifts.length > 0;

  const handleViewOrder = () => {
    setIsModalOpen(true);
  };

    const giftTypeLabel = (type) => {
    switch (type) {
      case 'digital-memory':
        return 'Digital Memory';
      case 'magnets-frame':
        return 'Memory Magnet';
      case 'audio-gift':
        return 'Audio Gift';
      default:
        return type || 'Future Gift';
    }
  };

  /* ── Status pill styling ── */
  const statusStyles = (() => {
    switch (resolvedStatus) {
      case 'processing':
      case 'in_production':
      case 'content_review':
        return 'bg-blue-100 text-blue-700';
      case 'shipped':
        return 'bg-purple-100 text-purple-700';
      case 'delivered':
        return 'bg-green-100 text-green-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  })();

  /* ── Human-readable status label ── */
  const statusLabel = (() => {
    switch (resolvedStatus) {
      case 'pending':
        return 'Pending';
      case 'processing':
        return 'Processing';
      case 'content_review':
        return 'In Review';
      case 'in_production':
        return 'In Production';
      case 'shipped':
        return 'Shipped';
      case 'delivered':
        return 'Delivered';
      case 'cancelled':
        return 'Cancelled';
      default:
        return resolvedStatus || 'Pending';
    }
  })();

  return (
    <>
      <div className="px-3.5 py-4 mb-4 border border-[#D9D9D9] max-w-2xl">
        {/* Order Header */}
        <div className="flex justify-between items-start border-b-[1.5px] border-[#E4E4E4] pb-3">
          <div className="flex flex-col">
            <span className="text-lg font-medium text-secondary">
              Order #{order.orderNumber || order.id}
            </span>
            <span className="text-sm text-[#6B635D] font-normal">
              {order.date} • {order.time}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <span
              className={`w-[5.3rem] h-7 rounded text-center flex items-center justify-center text-xs font-medium ${statusStyles}`}
            >
              {statusLabel}
            </span>
            <button
              onClick={handleViewOrder}
              className="text-sm flex items-center font-normal text-[#000] hover:text-gray-900"
            >
              View Order
              <ArrowRight className="ml-1" />
            </button>
          </div>
        </div>

        {/* Order Content */}
        <div className="flex gap-3.5 mt-5">
          {/* Product Image — first item's image, fall back to placeholder */}
          <div className="flex-shrink-0">
            <img
              src={items[0]?.image || MemoryMagnet}
              className="w-[8.7rem] h-[9.5rem] object-cover"
              alt="Product"
            />
          </div>

          {/* Product Details */}
          <div className="flex-1 flex flex-col">
            {/* Brand and Price Row */}
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-semibold text-[#1C2A46] uppercase text-lg mb-1">
                  {order.brand || items[0]?.slug }
                </h3>
                <p className="text-sm text-[#6B635D] italic">
                  {order.tagline || items[0]?.description || ''}
                </p>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold text-[#1C2A46]">
                  ₦{Number(order.total || 0).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Order Items — handles both string and object shapes */}
            <div className="mb-4">
              <ul className="space-y-1 text-[#1C2A46] text-sm">
                {items.map((item, index) => {
                  const label =
                    typeof item === 'string'
                      ? item
                      : `${item.title}${
                          item.quantity > 1 ? ` x${item.quantity}` : ''
                        }`;

                  return (
                    <li
                      key={item._id || item.productId || index}
                      className="flex items-start"
                    >
                      <span className="mr-2">•</span>
                      <span>{label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Future Gift Section — data-driven */}

{hasFutureGifts && (
  <div className="mt-auto">
    <div className="w-full bg-[#F5F5F5] p-4">
      <h4 className="font-medium text-[#1C2A46] mb-2">
        Future Gift Scheduled
      </h4>
      <ul className="space-y-1 text-[#1C2A46] text-sm">
        {futureGifts.map((gift, i) => (
          <li key={i} className="flex items-start justify-between gap-4">
            <span className="flex items-start min-w-0">
              <span className="mr-2">•</span>
              <span className="truncate uppercase">{giftTypeLabel(gift.giftType)}</span>
            </span>
            {gift.deliveryDate && (
              <span className="text-[#6B635D] flex-shrink-0 text-right">
                {new Date(gift.deliveryDate).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  </div>
)}
          </div>
        </div>
      </div>

      {/* Modal */}
      <OrderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        order={order}
      />
    </>
  );
};

export default OrderCard;